import { useEffect, useState } from 'react';
import { ApiError, api } from '../api/client';
import type { ClaimRequest } from '../api/types';
import { MODELS } from '../models';
import { IconCar, IconPlus } from './Icons';
import { useToast } from './Toast';
import { formatAgo, useT } from '../i18n';

/**
 * Lets a customer describe the vehicle they want added.
 *
 * They fill in the details because they are the ones holding the tracker and
 * the logbook; sending this grants nothing on its own, and an administrator
 * reviews — and can correct — everything before the device is registered.
 */
export function ClaimDevice({ onClaimed }: { onClaimed: () => void }) {
  const { t, lang } = useT();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    imei: '',
    name: '',
    model: 'GT06N' as string,
    plate: '',
    simNumber: '',
    hasImmobiliser: true,
    note: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<ClaimRequest[]>([]);

  const reload = async () => {
    try {
      setRequests(await api.claimRequests());
    } catch {
      // A failure here only costs the status list, not the ability to ask.
      setRequests([]);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.requestClaim({
        imei: form.imei.trim(),
        name: form.name.trim(),
        model: form.model,
        plate: form.plate.trim() || null,
        simNumber: form.simNumber.trim() || null,
        hasImmobiliser: form.hasImmobiliser,
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
      });
      toast('ok', t('requestSent'));
      setOpen(false);
      setForm({
        imei: '',
        name: '',
        model: 'GT06N',
        plate: '',
        simNumber: '',
        hasImmobiliser: true,
        note: '',
      });
      await reload();
      onClaimed();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('error'));
    } finally {
      setBusy(false);
    }
  };

  const pending = requests.filter((r) => r.status === 'pending');
  const rejected = requests.filter((r) => r.status === 'rejected').slice(0, 2);

  return (
    <>
      {(pending.length > 0 || rejected.length > 0) && (
        <ul className="request-list">
          {pending.map((r) => (
            <li key={r.id} className="request-row pending">
              <span className="request-status">{t('awaitingApproval')}</span>
              <span>{r.proposed_name ?? r.imei}</span>
              <span className="muted tiny">{formatAgo(r.created_at, lang)}</span>
            </li>
          ))}
          {rejected.map((r) => (
            <li key={r.id} className="request-row rejected">
              <span className="request-status">{t('requestRejected')}</span>
              <span>{r.proposed_name ?? r.imei}</span>
              {r.reason && <span className="muted tiny">{r.reason}</span>}
            </li>
          ))}
        </ul>
      )}

      <button type="button" className="btn block claim-trigger" onClick={() => setOpen(true)}>
        <IconPlus />
        {t('addMyVehicle')}
      </button>

      {open && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <form className="modal scrollable" onSubmit={submit}>
            <div
              className="modal-icon"
              style={{ background: 'var(--brand-soft)', color: 'var(--brand-500)' }}
            >
              <IconCar size={22} />
            </div>

            <h3>{t('addMyVehicle')}</h3>
            <p className="muted small" style={{ margin: 0 }}>
              {t('claimHelp')}
            </p>

            <label>
              {t('imei')}
              <input
                value={form.imei}
                onChange={(e) => setForm({ ...form, imei: e.target.value })}
                required
                dir="ltr"
                inputMode="numeric"
                pattern="\d{6,20}"
                placeholder="860000000000001"
                autoFocus
              />
            </label>

            <label>
              {t('vehicleName')}
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                maxLength={120}
                placeholder={t('vehicleNamePlaceholder')}
              />
            </label>

            <label>
              {t('model')}
              <select
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
              >
                {MODELS.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </label>

            <label>
              {t('plate')}
              <input
                value={form.plate}
                onChange={(e) => setForm({ ...form, plate: e.target.value })}
                maxLength={40}
              />
            </label>

            <label>
              {t('simNumber')}
              <input
                value={form.simNumber}
                onChange={(e) => setForm({ ...form, simNumber: e.target.value })}
                dir="ltr"
                inputMode="tel"
                maxLength={40}
              />
            </label>

            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={form.hasImmobiliser}
                onChange={(e) => setForm({ ...form, hasImmobiliser: e.target.checked })}
              />
              {t('hasImmobiliser')}
            </label>

            <label>
              {t('claimNote')}
              <input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                maxLength={300}
                placeholder={t('claimNotePlaceholder')}
              />
            </label>

            {error && <p className="form-error">{error}</p>}

            <div className="btn-grid">
              <button type="submit" className="btn primary" disabled={busy}>
                {busy && <span className="btn-spinner" />}
                {t('sendRequest')}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setOpen(false);
                  setError(null);
                }}
              >
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
